// src/pages/Reviewspage/Reviews.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { reviewsService } from "../../services";
import "./Reviews.css";

// Rating filter options
const RATING_OPTIONS = [
  { value: '', label: 'All Ratings' },
  { value: '5', label: '5 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '2', label: '2 Stars' },
  { value: '1', label: '1 Star' },
];

// Flag reason options
const FLAG_REASONS = [
  { value: 'inappropriate', label: 'Inappropriate Content' },
  { value: 'spam', label: 'Spam' },
  { value: 'fake', label: 'Fake Review' },
  { value: 'offensive', label: 'Offensive Language' },
  { value: 'other', label: 'Other' },
];

// Star component
const StarRow = ({ rating = 5, size = 'medium' }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(<span key={i} className="star-filled">★</span>);
    } else if (i === fullStars && hasHalf) {
      stars.push(<span key={i} className="star-half">★</span>);
    } else {
      stars.push(<span key={i} className="star-empty">☆</span>);
    }
  }

  return <div className={`mq-reviews-stars mq-stars-${size}`}>{stars}</div>;
};

export default function Reviews() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [flaggedReviews, setFlaggedReviews] = useState([]);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    breakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  });
  const [topDrivers, setTopDrivers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [flaggedPagination, setFlaggedPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  // View tabs
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'moderation'

  // Filters
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal states
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showHideModal, setShowHideModal] = useState(false);

  // Form states
  const [flagForm, setFlagForm] = useState({ reason: '', notes: '' });
  const [responseText, setResponseText] = useState('');
  const [hideReason, setHideReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Menu state
  const [activeMenu, setActiveMenu] = useState(null);

  // Fetch reviews
  const fetchReviews = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        page,
        limit: 10,
        rating: ratingFilter || undefined,
        sortBy,
        order: sortOrder,
      };

      const response = await reviewsService.getReviews(filters);

      setReviews(response.reviews || []);
      setPagination({
        page: response.pagination?.currentPage || page,
        totalPages: response.pagination?.totalPages || 1,
        total: response.pagination?.total || 0,
      });

    } catch (err) {
      console.error('Fetch reviews error:', err);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [ratingFilter, sortBy, sortOrder]);

  // Fetch flagged reviews
  const fetchFlaggedReviews = useCallback(async (page = 1) => {
    try {
      const response = await reviewsService.getFlaggedReviews({ page, limit: 10 });

      setFlaggedReviews(response.reviews || []);
      setFlaggedPagination({
        page: response.pagination?.page || page,
        totalPages: response.pagination?.totalPages || 1,
        total: response.pagination?.total || 0,
      });
    } catch (err) {
      console.error('Fetch flagged reviews error:', err);
    }
  }, []);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const statsData = await reviewsService.getStats();
      // Map the response to the expected format
      setStats({
        averageRating: statsData.stats?.avgRating || statsData.avgRating || 0,
        totalReviews: statsData.stats?.total || statsData.total || 0,
        breakdown: {
          5: statsData.stats?.fiveStar || statsData.fiveStar || 0,
          4: statsData.stats?.fourStar || statsData.fourStar || 0,
          3: statsData.stats?.threeStar || statsData.threeStar || 0,
          2: statsData.stats?.twoStar || statsData.twoStar || 0,
          1: statsData.stats?.oneStar || statsData.oneStar || 0,
        },
      });
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  };

  // Fetch top drivers
  const fetchTopDrivers = async () => {
    try {
      const drivers = await reviewsService.getDriverRatings(5);
      setTopDrivers(drivers);
    } catch (err) {
      console.error('Fetch driver ratings error:', err);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchReviews(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchReviews]);

  // Fetch stats, top drivers, and flagged reviews on mount
  useEffect(() => {
    fetchStats();
    fetchTopDrivers();
    fetchFlaggedReviews();
  }, [fetchFlaggedReviews]);

  // Handle flag review
  const handleFlagReview = async () => {
    if (!selectedReview || !flagForm.reason) {
      alert('Please select a reason');
      return;
    }

    try {
      setSaving(true);
      await reviewsService.flagReview(selectedReview._id, flagForm.reason, flagForm.notes);
      setShowFlagModal(false);
      setSelectedReview(null);
      setFlagForm({ reason: '', notes: '' });
      fetchReviews(pagination.page);
      fetchFlaggedReviews();
    } catch (err) {
      console.error('Flag review error:', err);
      alert('Failed to flag review');
    } finally {
      setSaving(false);
    }
  };

  // Handle unflag review
  const handleUnflagReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to unflag this review?')) return;

    try {
      await reviewsService.unflagReview(reviewId);
      setActiveMenu(null);
      fetchReviews(pagination.page);
      fetchFlaggedReviews();
    } catch (err) {
      console.error('Unflag review error:', err);
      alert('Failed to unflag review');
    }
  };

  // Handle admin response
  const handleAdminResponse = async () => {
    if (!selectedReview || !responseText.trim()) {
      alert('Please enter a response');
      return;
    }

    try {
      setSaving(true);
      await reviewsService.addAdminResponse(selectedReview._id, responseText);
      setShowResponseModal(false);
      setSelectedReview(null);
      setResponseText('');
      fetchReviews(pagination.page);
      fetchFlaggedReviews();
    } catch (err) {
      console.error('Add response error:', err);
      alert('Failed to add response');
    } finally {
      setSaving(false);
    }
  };

  // Handle hide review
  const handleHideReview = async () => {
    if (!selectedReview) return;

    try {
      setSaving(true);
      await reviewsService.hideReview(selectedReview._id, hideReason);
      setShowHideModal(false);
      setSelectedReview(null);
      setHideReason('');
      fetchReviews(pagination.page);
      fetchFlaggedReviews();
    } catch (err) {
      console.error('Hide review error:', err);
      alert('Failed to hide review');
    } finally {
      setSaving(false);
    }
  };

  // Handle unhide review
  const handleUnhideReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to unhide this review?')) return;

    try {
      await reviewsService.unhideReview(reviewId);
      setActiveMenu(null);
      fetchReviews(pagination.page);
    } catch (err) {
      console.error('Unhide review error:', err);
      alert('Failed to unhide review');
    }
  };

  // Handle delete review
  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) return;

    try {
      await reviewsService.deleteReview(reviewId);
      setActiveMenu(null);
      fetchReviews(pagination.page);
      fetchFlaggedReviews();
      fetchStats();
    } catch (err) {
      console.error('Delete review error:', err);
      alert('Failed to delete review');
    }
  };

  // Open modals
  const openFlagModal = (review) => {
    setSelectedReview(review);
    setFlagForm({ reason: '', notes: '' });
    setShowFlagModal(true);
    setActiveMenu(null);
  };

  const openResponseModal = (review) => {
    setSelectedReview(review);
    setResponseText(review.adminResponse || '');
    setShowResponseModal(true);
    setActiveMenu(null);
  };

  const openHideModal = (review) => {
    setSelectedReview(review);
    setHideReason('');
    setShowHideModal(true);
    setActiveMenu(null);
  };

  const openReviewDetails = (review) => {
    setSelectedReview(review);
    setShowReviewModal(true);
    setActiveMenu(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (activeTab === 'all') {
      if (newPage >= 1 && newPage <= pagination.totalPages) {
        fetchReviews(newPage);
      }
    } else {
      if (newPage >= 1 && newPage <= flaggedPagination.totalPages) {
        fetchFlaggedReviews(newPage);
      }
    }
  };

  // Calculate breakdown percentages
  const getBreakdownPercent = (rating) => {
    if (stats.totalReviews === 0) return 0;
    const count = stats.breakdown?.[rating] || 0;
    return Math.round((count / stats.totalReviews) * 100);
  };

  // Filter reviews by search
  const filteredReviews = reviews.filter(review => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      review.comment?.toLowerCase().includes(searchLower) ||
      review.client?.firstName?.toLowerCase().includes(searchLower) ||
      review.driver?.firstName?.toLowerCase().includes(searchLower)
    );
  });

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Get current reviews based on active tab
  const currentReviews = activeTab === 'all' ? filteredReviews : flaggedReviews;
  const currentPagination = activeTab === 'all' ? pagination : flaggedPagination;

  return (
    <div className="reviews-page-container">
      {/* Header */}
      <div className="mq-welcome-block">
        <button className="mq-back-btn" onClick={() => navigate('/dashboard')}>
          <span className="mq-back-btn-icon">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Back Home
        </button>
        <h1 className="mq-welcome-name">Reviews</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="reviews-error">
          <span>{error}</span>
          <button onClick={() => fetchReviews(pagination.page)}>Retry</button>
        </div>
      )}

      {/* Summary Grid */}
      <div className="mq-reviews-grid">
        {/* LEFT: Overall score & breakdown */}
        <section className="mq-card mq-reviews-summary">
          <div className="mq-reviews-score-row">
            <div>
              <div className="mq-reviews-score">
                {stats.averageRating?.toFixed(1) || '0.0'} / 5.0
              </div>
              <StarRow rating={stats.averageRating || 0} />
              <div className="mq-reviews-count">
                {stats.totalReviews || 0} reviews
              </div>
            </div>
          </div>

          <div className="mq-reviews-breakdown">
            {[5, 4, 3, 2, 1].map((rating) => (
              <div className="mq-review-bar-row" key={rating}>
                <span className="mq-review-bar-label">{rating}</span>
                <div className="mq-review-bar-track">
                  <div
                    className="mq-review-bar-fill"
                    style={{ width: `${getBreakdownPercent(rating)}%` }}
                  />
                </div>
                <span className="mq-review-bar-percent">
                  {getBreakdownPercent(rating)}%
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* RIGHT: Top rated drivers */}
        <section className="mq-card mq-reviews-drivers">
          <div className="mq-reviews-drivers-head">
            <span>Top Rated Drivers</span>
            <span>Rating</span>
          </div>

          <div className="mq-reviews-drivers-body">
            {topDrivers.length === 0 ? (
              <div className="reviews-empty-drivers">No driver ratings yet</div>
            ) : (
              topDrivers.map((driver) => (
                <div className="mq-reviews-driver-row" key={driver._id}>
                  <div className="mq-reviews-driver-main">
                    <div className="mq-reviews-avatar-placeholder">
                      {driver.firstName?.[0] || 'D'}
                    </div>
                    <div className="mq-reviews-driver-text">
                      <div className="mq-reviews-driver-name">
                        {driver.firstName} {driver.lastName}
                      </div>
                      <div className="mq-reviews-driver-route">
                        {driver.totalReviews || 0} reviews
                      </div>
                    </div>
                  </div>
                  <div className="mq-reviews-driver-rating">
                    <StarRow rating={driver.averageRating || 0} size="small" />
                    <span className="reviews-rating-value">
                      {(driver.averageRating || 0).toFixed(1)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Reviews List */}
      <section className="mq-card reviews-list-card">
        {/* Tabs */}
        <div className="reviews-tabs">
          <button
            className={`reviews-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Reviews <span className="reviews-tab-count">{pagination.total}</span>
          </button>
          <button
            className={`reviews-tab ${activeTab === 'moderation' ? 'active' : ''}`}
            onClick={() => setActiveTab('moderation')}
          >
            Moderation Queue
            {flaggedPagination.total > 0 && (
              <span className="reviews-tab-badge">{flaggedPagination.total}</span>
            )}
          </button>
        </div>

        <div className="reviews-list-header">
          <h3 className="reviews-list-title">
            {activeTab === 'all' ? 'All Reviews' : 'Flagged Reviews'}
          </h3>

          {activeTab === 'all' && (
            <div className="reviews-controls">
              <div className="reviews-search-box">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="6.5" cy="6.5" r="5" stroke="#9ca3af" strokeWidth="1.5"/>
                  <path d="M10 10L14 14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  className="reviews-search-input"
                  placeholder="Search reviews..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="reviews-search-clear" onClick={() => setSearch('')}>×</button>
                )}
              </div>

              <select
                className="reviews-filter-select"
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value)}
              >
                {RATING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <select
                className="reviews-filter-select"
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
              >
                <option value="createdAt-desc">Newest First</option>
                <option value="createdAt-asc">Oldest First</option>
                <option value="rating-desc">Highest Rating</option>
                <option value="rating-asc">Lowest Rating</option>
              </select>
            </div>
          )}
        </div>

        {/* Reviews Table */}
        {loading && currentReviews.length === 0 ? (
          <div className="reviews-loading">
            <div className="reviews-spinner"></div>
            <p>Loading reviews...</p>
          </div>
        ) : currentReviews.length === 0 ? (
          <div className="reviews-empty">
            <p>{activeTab === 'all' ? 'No reviews found' : 'No flagged reviews'}</p>
            <small>
              {activeTab === 'all'
                ? 'Reviews will appear here when customers submit them'
                : 'Flagged reviews will appear here for moderation'}
            </small>
          </div>
        ) : (
          <div className="mq-table-scroll"><table className="reviews-data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Driver</th>
                <th>Rating</th>
                <th>Comment</th>
                {activeTab === 'moderation' && <th>Flag Reason</th>}
                <th>Status</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {currentReviews.map((review) => (
                <tr
                  key={review._id}
                  className={`reviews-table-row ${review.isHidden ? 'review-hidden' : ''} ${review.isFlagged ? 'review-flagged' : ''}`}
                >
                  <td>
                    <div className="reviews-customer-cell">
                      <span className="reviews-customer-name">
                        {review.client?.firstName || 'Unknown'} {review.client?.lastName || ''}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="reviews-driver-cell">
                      <span className="reviews-driver-name">
                        {review.driver?.firstName || 'N/A'} {review.driver?.lastName || ''}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="reviews-rating-cell">
                      <StarRow rating={review.rating} size="small" />
                      <span className="reviews-rating-number">{review.rating}</span>
                    </div>
                  </td>
                  <td className="reviews-comment-cell">
                    <p className="reviews-comment-text">
                      {review.comment?.substring(0, 80)}
                      {review.comment?.length > 80 ? '...' : ''}
                    </p>
                    {review.adminResponse && (
                      <p className="reviews-admin-response-preview">
                        Admin: {review.adminResponse.substring(0, 50)}...
                      </p>
                    )}
                  </td>
                  {activeTab === 'moderation' && (
                    <td>
                      <span className={`reviews-flag-badge flag-${review.flagReason}`}>
                        {review.flagReason || 'N/A'}
                      </span>
                    </td>
                  )}
                  <td>
                    <div className="reviews-status-badges">
                      {review.isHidden && (
                        <span className="reviews-status-badge status-hidden">Hidden</span>
                      )}
                      {review.isFlagged && !review.isHidden && (
                        <span className="reviews-status-badge status-flagged">Flagged</span>
                      )}
                      {review.adminResponse && (
                        <span className="reviews-status-badge status-responded">Responded</span>
                      )}
                      {!review.isHidden && !review.isFlagged && !review.adminResponse && (
                        <span className="reviews-status-badge status-active">Active</span>
                      )}
                    </div>
                  </td>
                  <td>{formatDate(review.createdAt)}</td>
                  <td className="reviews-col-menu">
                    <button
                      className="reviews-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === review._id ? null : review._id);
                      }}
                    >
                      ⋮
                    </button>
                    {activeMenu === review._id && (
                      <div className="reviews-menu-dropdown" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => openReviewDetails(review)}>View Details</button>
                        <button onClick={() => openResponseModal(review)}>
                          {review.adminResponse ? 'Edit Response' : 'Add Response'}
                        </button>
                        {review.isFlagged ? (
                          <button onClick={() => handleUnflagReview(review._id)}>Unflag</button>
                        ) : (
                          <button onClick={() => openFlagModal(review)}>Flag Review</button>
                        )}
                        {review.isHidden ? (
                          <button onClick={() => handleUnhideReview(review._id)}>Unhide</button>
                        ) : (
                          <button onClick={() => openHideModal(review)}>Hide Review</button>
                        )}
                        <button className="reviews-menu-delete" onClick={() => handleDeleteReview(review._id)}>
                          Delete Review
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}

        {/* Pagination */}
        {currentReviews.length > 0 && (
          <div className="reviews-footer-section">
            <span className="reviews-footer-text">
              Page {currentPagination.page} of {currentPagination.totalPages}
            </span>
            <div className="reviews-pagination-controls">
              <button
                className="reviews-pagination-btn"
                onClick={() => handlePageChange(currentPagination.page - 1)}
                disabled={currentPagination.page <= 1}
              >
                Prev
              </button>
              {[...Array(Math.min(5, currentPagination.totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    className={`reviews-pagination-btn ${currentPagination.page === pageNum ? 'reviews-pagination-active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                className="reviews-pagination-btn"
                onClick={() => handlePageChange(currentPagination.page + 1)}
                disabled={currentPagination.page >= currentPagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Review Details Modal */}
      {showReviewModal && selectedReview && (
        <div className="reviews-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="reviews-modal reviews-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="reviews-modal-header">
              <h3>Review Details</h3>
              <button className="reviews-modal-close" onClick={() => setShowReviewModal(false)}>×</button>
            </div>
            <div className="reviews-modal-body">
              <div className="reviews-detail-rating">
                <StarRow rating={selectedReview.rating} size="large" />
                <span className="reviews-detail-rating-number">{selectedReview.rating} / 5</span>
              </div>

              <div className="reviews-detail-grid">
                <div className="reviews-detail-item">
                  <label>Customer</label>
                  <span>
                    {selectedReview.client?.firstName || 'Unknown'} {selectedReview.client?.lastName || ''}
                  </span>
                </div>
                <div className="reviews-detail-item">
                  <label>Driver</label>
                  <span>
                    {selectedReview.driver?.firstName || 'N/A'} {selectedReview.driver?.lastName || ''}
                  </span>
                </div>
                <div className="reviews-detail-item">
                  <label>Job</label>
                  <span>#{selectedReview.job?.jobNumber || selectedReview.job?._id?.slice(-6) || 'N/A'}</span>
                </div>
                <div className="reviews-detail-item">
                  <label>Date</label>
                  <span>{formatDate(selectedReview.createdAt)}</span>
                </div>
                <div className="reviews-detail-item full-width">
                  <label>Comment</label>
                  <p className="reviews-detail-comment">{selectedReview.comment || 'No comment provided'}</p>
                </div>
                {selectedReview.adminResponse && (
                  <div className="reviews-detail-item full-width">
                    <label>Admin Response</label>
                    <p className="reviews-detail-admin-response">{selectedReview.adminResponse}</p>
                    <small className="reviews-response-meta">
                      Responded on {formatDate(selectedReview.adminResponseAt)}
                    </small>
                  </div>
                )}
                {selectedReview.isFlagged && (
                  <div className="reviews-detail-item full-width reviews-flag-info">
                    <label>Flag Information</label>
                    <p>
                      <strong>Reason:</strong> {selectedReview.flagReason}
                      {selectedReview.flagNotes && <><br /><strong>Notes:</strong> {selectedReview.flagNotes}</>}
                    </p>
                    <small>Flagged on {formatDate(selectedReview.flaggedAt)}</small>
                  </div>
                )}
              </div>
            </div>
            <div className="reviews-modal-footer">
              <button className="reviews-btn-outline" onClick={() => openResponseModal(selectedReview)}>
                {selectedReview.adminResponse ? 'Edit Response' : 'Add Response'}
              </button>
              {!selectedReview.isFlagged && (
                <button className="reviews-btn-warning" onClick={() => openFlagModal(selectedReview)}>
                  Flag Review
                </button>
              )}
              <button
                className="reviews-btn-danger"
                onClick={() => {
                  handleDeleteReview(selectedReview._id);
                  setShowReviewModal(false);
                }}
              >
                Delete
              </button>
              <button className="reviews-btn-primary" onClick={() => setShowReviewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {showFlagModal && selectedReview && (
        <div className="reviews-modal-overlay" onClick={() => setShowFlagModal(false)}>
          <div className="reviews-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reviews-modal-header">
              <h3>Flag Review</h3>
              <button className="reviews-modal-close" onClick={() => setShowFlagModal(false)}>×</button>
            </div>
            <div className="reviews-modal-body">
              <div className="reviews-flag-preview">
                <StarRow rating={selectedReview.rating} size="small" />
                <p>"{selectedReview.comment?.substring(0, 100)}..."</p>
              </div>

              <div className="reviews-form-group">
                <label>Flag Reason *</label>
                <select
                  value={flagForm.reason}
                  onChange={(e) => setFlagForm({ ...flagForm, reason: e.target.value })}
                >
                  <option value="">Select a reason</option>
                  {FLAG_REASONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="reviews-form-group">
                <label>Additional Notes</label>
                <textarea
                  value={flagForm.notes}
                  onChange={(e) => setFlagForm({ ...flagForm, notes: e.target.value })}
                  placeholder="Add any additional context..."
                  rows="3"
                />
              </div>
            </div>
            <div className="reviews-modal-footer">
              <button className="reviews-btn-outline" onClick={() => setShowFlagModal(false)}>
                Cancel
              </button>
              <button
                className="reviews-btn-warning"
                onClick={handleFlagReview}
                disabled={saving || !flagForm.reason}
              >
                {saving ? 'Flagging...' : 'Flag Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedReview && (
        <div className="reviews-modal-overlay" onClick={() => setShowResponseModal(false)}>
          <div className="reviews-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reviews-modal-header">
              <h3>{selectedReview.adminResponse ? 'Edit Response' : 'Add Response'}</h3>
              <button className="reviews-modal-close" onClick={() => setShowResponseModal(false)}>×</button>
            </div>
            <div className="reviews-modal-body">
              <div className="reviews-flag-preview">
                <StarRow rating={selectedReview.rating} size="small" />
                <p>"{selectedReview.comment?.substring(0, 100)}..."</p>
                <small>By {selectedReview.client?.firstName} {selectedReview.client?.lastName}</small>
              </div>

              <div className="reviews-form-group">
                <label>Admin Response *</label>
                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Write your response to this review..."
                  rows="5"
                />
              </div>
            </div>
            <div className="reviews-modal-footer">
              <button className="reviews-btn-outline" onClick={() => setShowResponseModal(false)}>
                Cancel
              </button>
              <button
                className="reviews-btn-primary"
                onClick={handleAdminResponse}
                disabled={saving || !responseText.trim()}
              >
                {saving ? 'Saving...' : 'Save Response'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide Modal */}
      {showHideModal && selectedReview && (
        <div className="reviews-modal-overlay" onClick={() => setShowHideModal(false)}>
          <div className="reviews-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reviews-modal-header">
              <h3>Hide Review</h3>
              <button className="reviews-modal-close" onClick={() => setShowHideModal(false)}>×</button>
            </div>
            <div className="reviews-modal-body">
              <div className="reviews-hide-warning">
                <p>This will hide the review from public display. The review will still exist in the system.</p>
              </div>

              <div className="reviews-form-group">
                <label>Reason for Hiding</label>
                <textarea
                  value={hideReason}
                  onChange={(e) => setHideReason(e.target.value)}
                  placeholder="Enter reason for hiding this review (optional)"
                  rows="3"
                />
              </div>
            </div>
            <div className="reviews-modal-footer">
              <button className="reviews-btn-outline" onClick={() => setShowHideModal(false)}>
                Cancel
              </button>
              <button
                className="reviews-btn-warning"
                onClick={handleHideReview}
                disabled={saving}
              >
                {saving ? 'Hiding...' : 'Hide Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
